import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type {
  Acceptance_Status_Enum,
  UpdateAcceptanceMutation,
  UpdateAcceptanceMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateAcceptanceDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

type UpdateAcceptanceTRPCInput = Omit<
  UpdateAcceptanceMutationVariables,
  'Title' | 'Details' | 'Status' | 'CustomAttributeData'
> & {
  Title: string;
  Details: string;
  Status: Acceptance_Status_Enum;
  CustomAttributeData?: CustomAttributeData | null;
};

/**
 * Maps the tRPC mutation response to the GraphQL mutation structure. The applied
 * entity is discarded — the legacy shape reports a fixed affected-row count.
 */
const mapTrpcResponseToGraphQL = (_applied: {
  Id: string;
}): UpdateAcceptanceMutation => ({
  updateChildAcceptance: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

export const useUpdateAcceptance = createMutationHook({
  name: 'updateAcceptance',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.acceptance.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateAcceptanceMutationVariables
  ): UpdateAcceptanceTRPCInput => {
    // `OriginalTimestamp` rides along to the tRPC path, which pins it in the
    // repository's WHERE (COI-622). The legacy path advertises the same token
    // and then ignores it, so V3 is the only side that actually locks.
    const { Title, Details, Status, ...rest } = variables;
    if (!Status) {
      throw new Error('Status is required for acceptance update');
    }
    if (Title == null || Details == null) {
      throw new Error('Title and Details are required for acceptance update');
    }

    return {
      ...rest,
      Title,
      Details,
      Status,
      CustomAttributeData: rest.CustomAttributeData ?? null,
    };
  },
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.acceptance.register.queryKey(),
    trpc.frontend.acceptance.getById.queryKey(),
    trpc.frontend.acceptance.getByParentRiskId.queryKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are success.
  // Echo the input Id on the CR branch (the entity is unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string }>,
    input: UpdateAcceptanceMutationVariables
  ) => mapTrpcResponseToGraphQL(unwrapApprovalGated(result, { Id: input.Id })),
  graphqlDocument: UpdateAcceptanceDocument,
  graphqlVariables: (variables: UpdateAcceptanceMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['acceptance', 'change_request'],
  refetchQueries: [namedOperations.Query.getAcceptanceById],
});
